/* How a product is actually made: the steps, and the temperatures they
 * happen at.
 *
 * Pure functions only, no Supabase — the same split as formulaModel.js, so
 * the parsing and the formatting can be tested without a database.
 *
 * Both fields live on the family (see 0031_formula_method.sql): the method
 * is how you make the product, not part of the costed recipe, so editing it
 * must not mint a new formula version.
 */

export const TEMP_UNITS = ['C', 'F'];

// Numbering the user typed themselves. Steps are rendered as an <ol>, so a
// line that already starts "1." would otherwise print as "1. 1. Melt the
// oils" — and the numbers would go wrong the moment a step is inserted.
const TYPED_NUMBERING = /^\s*(?:\d+[.)]|[-*•])\s+/;

// One step per line. Blank lines are separators people type for breathing
// room, never steps.
export function parseSteps(text) {
  if (!text) return [];
  return String(text)
    .split('\n')
    .map((line) => line.replace(TYPED_NUMBERING, '').trim())
    .filter(Boolean);
}

export function stepsToText(steps) {
  return (steps || []).join('\n');
}

// An empty field is not a temperature. Number('') and Number(null) are both
// 0 — which is finite, and 0 °C is a perfectly real temperature — so a blank
// input would otherwise be indistinguishable from someone deliberately
// entering freezing. Reject absence before converting, never after.
function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Drops anything half-entered rather than rendering "undefined °C" on a
// sheet someone is following at the bench.
export function normaliseTemps(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => ({
      label: String(row?.label ?? '').trim(),
      value: toNumber(row?.value),
      max: toNumber(row?.max),
      unit: TEMP_UNITS.includes(row?.unit) ? row.unit : 'C',
    }))
    .filter((row) => row.label && row.value !== null)
    // A range entered backwards ("50 to 45") is a slip, not a different
    // range; showing it as typed would read as an error on the sheet.
    .map((row) =>
      row.max != null && row.max < row.value ? { ...row, value: row.max, max: row.value } : row
    );
}

export function convertTemp(value, from, to) {
  if (!Number.isFinite(value)) return null;
  if (from === to) return value;
  return from === 'C' ? value * (9 / 5) + 32 : (value - 32) * (5 / 9);
}

// One decimal at most: a thermometer at the bench doesn't resolve finer,
// and 45.000000001 °C is noise.
const round = (n) => (Number.isFinite(n) ? Math.round(n * 10) / 10 : null);

export function formatTemp(temp) {
  const value = toNumber(temp?.value);
  if (value === null) return '';
  const unit = TEMP_UNITS.includes(temp.unit) ? temp.unit : 'C';
  const low = round(value);
  const high = toNumber(temp.max) === null ? null : round(toNumber(temp.max));
  const span = high != null && high !== low ? `${low}–${high}` : `${low}`;
  return `${span} °${unit}`;
}

// The other scale in brackets. Thermometers and printed references disagree
// on which unit they use, and converting in your head over a hot pot is how
// a batch gets ruined.
export function formatTempWithAlt(temp) {
  const primary = formatTemp(temp);
  if (!primary) return '';
  const unit = TEMP_UNITS.includes(temp.unit) ? temp.unit : 'C';
  const other = unit === 'C' ? 'F' : 'C';
  const low = round(convertTemp(toNumber(temp.value), unit, other));
  const max = toNumber(temp.max);
  const high = max === null ? null : round(convertTemp(max, unit, other));
  const span = high != null && high !== low ? `${low}–${high}` : `${low}`;
  return `${primary} (${span} °${other})`;
}

export function hasMethod(family) {
  return parseSteps(family?.method).length > 0 || normaliseTemps(family?.key_temps).length > 0;
}
